import { useMemo } from "react"
import clsx from "clsx"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Divider,
  List,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"

import { PageCard, PageCardContent } from "components/card"
import { Carousel, CarouselItem } from "components/Carousel"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { ImageCarouselModal } from "components/Image"
import { InfoItem } from "components/InfoItem"
import { QRCode } from "components/QRCode"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { useTranslation } from "providers/i18n"
import { getDurationDate } from "utils/date"
import { IEventDetail, IPromotionDetail } from "utils/feature"
import { ImdfFeature } from "providers/venue/types"
import { deepmerge } from "@mui/utils"

// TODO: will move the extended fields into `IEventDetail` / `IPromotionDetail` in future
interface IEventDetailItem extends IEventDetail {
  feature: ImdfFeature
  descriptionText: string
  openingText: string
}

interface IPromotionDetailItem extends IPromotionDetail {
  feature: ImdfFeature
  descriptionText: string
  openingText: string
}

export interface IDetailPageProps {
  sx?: SxProps
  item: IEventDetailItem | IPromotionDetailItem
  headerDetail?: string
  navigationButtonText?: string
  mobileUrl?: string
  onBack?: () => void
  onViewLocation?: () => void
}

export const DetailPage = ({
  sx,
  item,
  headerDetail,
  navigationButtonText,
  mobileUrl,
  onBack = () => {},
  onViewLocation,
}: IDetailPageProps) => {
  const { t } = useTranslation()

  const theme = useTheme()

  const styleOverrides = theme.components[`DetailPage`]?.styleOverrides || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    carouselCard,
    cardHeader,
    qrCodeContainer,
    qrCode,
    qrCodeTextContainer,
    qrCodeTextPrimary,
    qrCodeTextSecondary,
    cardContentWrapper,
    infoContainer,
    infoItem,
    infoDivider,
    cardContentDetail,
    cardContentDetailHeader,
    cardContentDetailDescription,
    buttonContainer,
    buttonBack,
    buttonViewLocation,
    galleryCarouselModal,
  } = styleOverrides

  const { galleryCarouselOption, galleryModalCarouselOption } =
    theme.components[`DetailPage`]?.defaultProps || {}

  const hasManyImages = item?.allImages.length > 1

  const galleryUrls = useMemo(
    () =>
      item?.allImages.length > 0
        ? item?.allImages?.map((image) => image?.url)
        : [],
    [item?.allImages]
  )

  const { handleImage, openModal, activeImage, handleClose } = useImageModal()

  const title = t(item?.name)
  const durationPrimary = getDurationDate(item?.startDate, item?.endDate)
  const durationSecondary = item?.openingText
  const locationFeature = item?.feature
  const descriptionHtml = item?.descriptionText
  const hasLocation = !!item?.hasLocation
  const headerText = headerDetail || t("Detail")
  const showAlways = item?.showAlways
  const category = item?.category

  return (
    <>
      <PageCard sx={deepmerge(pageCard, sx)} className="DetailPage-pageCard">
        <PageCardContent
          sx={pageCardContent}
          className="DetailPage-pageCardContent"
        >
          <Box sx={carouselContainer} className="DetailPage-carouselContainer">
            {item?.allImages.length > 0 && (
              <Carousel
                sx={carousel}
                className="DetailPage-carousel"
                options={{
                  type: "fade",
                  pagination: true,
                  autoplay: true,
                  padding: 0,
                  gap: 0,
                  drag: hasManyImages,
                  ...galleryCarouselOption,
                }}
              >
                {item?.allImages.map(({ id, url }) => (
                  <CarouselItem key={`slide-${id}`}>
                    <CardMedia
                      sx={carouselCard}
                      className={clsx("DetailPage-carouselCard", category)}
                      component="img"
                      src={url}
                      onClick={() => handleImage(url)}
                    />
                  </CarouselItem>
                ))}
              </Carousel>
            )}
          </Box>

          <Card
            sx={cardContentWrapper}
            className="DetailPage-cardContentWrapper"
          >
            <CardHeader
              title={title}
              sx={cardHeader}
              className="DetailPage-cardHeader"
            />
            <List sx={infoContainer} className="DetailPage-infoContainer">
              {!showAlways && (
                <InfoItem
                  sx={infoItem}
                  title={t("Duration")}
                  primary={durationPrimary}
                  secondary={durationSecondary}
                />
              )}
              {hasLocation ? (
                <>
                  <Divider
                    sx={infoDivider}
                    className="DetailPage-infoDivider"
                  />
                  <InfoItem
                    sx={infoItem}
                    primary={
                      <LocationInfo
                        feature={locationFeature}
                        component="span"
                      />
                    }
                    title={t("Location")}
                  />
                </>
              ) : null}
            </List>

            {descriptionHtml && (
              <Typography
                sx={cardContentDetailHeader}
                className="DetailPage-cardContentDetailHeader"
              >
                {headerText}
              </Typography>
            )}
            <CardContent
              sx={cardContentDetail}
              className="DetailPage-cardContentDetail"
            >
              {!!descriptionHtml && (
                <Typography
                  variant="body2"
                  sx={cardContentDetailDescription}
                  className="DetailPage-cardContentDetailDescription"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              )}
            </CardContent>
            {mobileUrl && (
              <Box sx={qrCodeContainer} className="DetailPage-qrCodeContainer">
                <QRCode
                  sx={qrCode}
                  className="DetailPage-qrCode"
                  url={mobileUrl}
                />
                <Stack sx={qrCodeTextContainer}>
                  <Typography
                    variant="subtitle2"
                    component="p"
                    sx={qrCodeTextPrimary}
                  >
                    {t("Scan Here")}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={qrCodeTextSecondary}
                  >
                    {t("For Mobile")}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Card>

          <Box sx={buttonContainer} className="DetailPage-buttonContainer">
            <Button
              variant="outlined"
              sx={buttonBack}
              className="DetailPage-buttonBack"
              onClick={onBack}
            >
              <Typography>{t("Back")}</Typography>
            </Button>
            {hasLocation && onViewLocation ? (
              <Button
                variant="contained"
                sx={buttonViewLocation}
                className="DetailPage-buttonViewLocation"
                onClick={onViewLocation}
              >
                <Typography>
                  {t(navigationButtonText || "View Location")}
                </Typography>
              </Button>
            ) : null}
          </Box>
        </PageCardContent>
      </PageCard>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
        sx={galleryCarouselModal}
        carouselOptions={galleryModalCarouselOption}
      />
    </>
  )
}
